<template>
  <div
    v-if="visible"
    class="auto-extension-countdown d-flex align-center"
  >
    <v-progress-circular
      v-if="isProcessing"
      indeterminate
      size="14"
      width="2"
      class="auto-extension-countdown__mark mr-1"
    />
    <v-icon
      v-else
      left
      small
      class="auto-extension-countdown__mark"
    >
      {{ icons.mdiClockAlertOutline }}
    </v-icon>
    <span class="auto-extension-countdown__label">{{ label }}</span>
    <v-btn
      v-if="!isProcessing"
      x-small
      outlined
      class="auto-extension-countdown__stop-btn ml-2"
      :disabled="submitting"
      @click="dialog = true"
    >
      自動延長を止める
    </v-btn>

    <v-dialog
      :value="dialog"
      max-width="360"
      @input="dialog = $event"
    >
      <v-card>
        <v-card-title class="text-h6">
          自動延長を止めますか？
        </v-card-title>
        <v-card-text>
          この卓の自動延長をオフにします。終了予定を過ぎても自動で延長されません。
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            :disabled="submitting"
            @click="dialog = false"
          >
            やめる
          </v-btn>
          <v-btn
            class="auto-extension-countdown__confirm-btn"
            :loading="submitting"
            @click="stop"
          >
            止める
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { ref, watch } from '@vue/composition-api'
import { mdiClockAlertOutline } from '@mdi/js'
import { useTableUpdate } from '@/views/composable/useTableUpdate'
import {
  AUTO_EXTENSION_NOTICE_MINUTES,
  useAutoExtensionStatus,
} from '@/views/composable/useAutoExtensionCountdown'

export default {
  props: {
    tableId: {
      type: [Number, String],
      required: true,
    },
    expireAt: {
      type: String,
      default: null,
    },
    autoExtensionEnabled: {
      type: Boolean,
      default: false,
    },
    // 自動延長で実際に使われる延長コース名。解決できていない（未設定）卓では
    // サーバーが自動延長ジョブを予約しないため、予告も処理中表示も一切出さない。
    autoExtensionCourseName: {
      type: String,
      default: null,
    },
    noticeMinutes: {
      type: [Number, String],
      // 発火は終了の1分前固定。呼び出し側が省いても真実の値になるよう定数を既定にする。
      default: AUTO_EXTENSION_NOTICE_MINUTES,
    },
  },
  setup(props, { emit }) {
    const dialog = ref(false)
    const { submitting, run } = useTableUpdate(() => props.tableId)

    const { isProcessing, visible, label } = useAutoExtensionStatus({
      enabled: () => props.autoExtensionEnabled,
      expireAt: () => props.expireAt,
      courseName: () => props.autoExtensionCourseName,
      noticeMinutes: () => props.noticeMinutes,
    })

    // 呼び出し側がバナー表示ぶんのレイアウト（ヘッダー相殺など）を調整できるよう表示状態を通知する
    watch(visible, isVisible => emit('update:visible', isVisible), { immediate: true })

    const stop = async () => {
      const table = await run(
        { autoExtensionEnabled: false },
        { successMessage: 'この卓の自動延長を止めました', errorMessage: '自動延長の停止に失敗しました' },
      )

      if (table) {
        emit('updated:table', table)
        dialog.value = false
      }
    }

    return {
      icons: { mdiClockAlertOutline },
      submitting,
      dialog,
      visible,
      label,
      isProcessing,
      stop,
    }
  },
}
</script>

<style lang="scss" scoped>
// NOTE: color-pack無効のため `color="amber darken-2"` 等は効かない。警告色はCSS変数＋独自クラスで当てる
.auto-extension-countdown {
  --auto-extension-warn: #ff8f00;
  --auto-extension-warn-strong: #ff6f00;
}

.auto-extension-countdown__mark.v-icon,
.auto-extension-countdown__mark.v-progress-circular {
  color: var(--auto-extension-warn) !important;
  caret-color: var(--auto-extension-warn) !important;
}

.auto-extension-countdown__stop-btn.v-btn {
  color: var(--auto-extension-warn-strong) !important;
  border-color: currentColor !important;
}

// NOTE: v-dialogの中身はマウント時にapp直下へ移動するため、CSS変数ではなく実値で指定する
.auto-extension-countdown__confirm-btn.v-btn {
  background-color: #ff6f00 !important;
  color: #fff !important;
}
</style>
